// AuthScreen — login + signup + forgot password (Tailwind v4).
//
// Mantém a paleta do questionador (slate-50 bg, indigo-600 accents) num layout
// centralizado. Single component com `mode` toggle entre login/signup/forgot.

import { useState, useEffect, useRef, type FormEvent } from 'react';
import { Layers } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { authApi, ApiError } from '../lib/api';

type Mode = 'login' | 'signup' | 'forgot';

// 2026-05-20 Phase 2: timeout pro await de login/signup. Em rede ruim
// (3-15s), sem timeout o user ficava com o botão preso em "Aguarde…"
// sem feedback. 30s é o cap razoável — depois aborta + mostra erro.
const AUTH_TIMEOUT_MS = 30_000;

export default function AuthScreen() {
  const { login, signup } = useAuth();
  const [mode, setMode] = useState<Mode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  // 2026-05-20 Phase 2: mountedRef bloqueia setState após unmount.
  // Sintoma sem isso: user navega durante o await, component desmonta;
  // quando response chega, finally chama setSubmitting(false) em componente
  // morto → React warning + form fica em estado inconsistente se reabrir.
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!mountedRef.current) return;
    setError(null);
    setInfo(null);
    setSubmitting(true);

    // Timeout via setTimeout — se passar AUTH_TIMEOUT_MS, mostra erro.
    // (login/signup do useAuth não aceitam AbortSignal ainda; fix pleno
    // requereria estender authApi.* pra propagar signal pro fetch — V2.)
    let timedOut = false;
    const timeoutId = window.setTimeout(() => {
      timedOut = true;
      if (mountedRef.current) {
        setError('Tempo esgotado. Verifique sua conexão e tente novamente.');
        setSubmitting(false);
      }
    }, AUTH_TIMEOUT_MS);

    try {
      if (mode === 'login') {
        await login(email.trim(), password);
      } else if (mode === 'signup') {
        await signup(email.trim(), password);
      } else {
        await authApi.forgotPassword(email.trim());
        if (mountedRef.current && !timedOut) {
          setInfo('Se a conta existir, um e-mail com instruções foi enviado.');
        }
      }
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : (err as Error).message ?? 'Erro inesperado';
      if (mountedRef.current && !timedOut) setError(msg);
    } finally {
      window.clearTimeout(timeoutId);
      if (mountedRef.current && !timedOut) setSubmitting(false);
    }
  }

  const heading = mode === 'login' ? 'Entrar' : mode === 'signup' ? 'Criar conta' : 'Recuperar senha';
  const subhead = mode === 'login'
    ? 'Acesse seu painel NanoApp.'
    : mode === 'signup'
    ? 'Crie sua conta gratuita.'
    : 'Enviaremos um link para redefinir sua senha.';
  const submitLabel = mode === 'login' ? 'Entrar' : mode === 'signup' ? 'Criar conta' : 'Enviar link';

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10 bg-slate-50">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-6">
          <div className="w-12 h-12 rounded-2xl bg-indigo-600 flex items-center justify-center text-white shadow-lg">
            <Layers size={22} />
          </div>
          <h1 className="mt-3 text-xl font-bold text-slate-900">NanoApp</h1>
          <p className="text-sm text-slate-500">Construtor de formulários</p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-900">{heading}</h2>
          <p className="text-sm text-slate-500 mb-5">{subhead}</p>

          <form onSubmit={handleSubmit} className="flex flex-col gap-3">
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-slate-700">Email</span>
              <input
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                placeholder="seu@email.com"
              />
            </label>

            {mode !== 'forgot' && (
              <label className="flex flex-col gap-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-slate-700">Senha</span>
                  {mode === 'login' && (
                    <button
                      type="button"
                      onClick={() => { setMode('forgot'); setError(null); setInfo(null); }}
                      className="text-xs text-indigo-600 hover:underline"
                    >
                      Esqueci minha senha
                    </button>
                  )}
                </div>
                <input
                  type="password"
                  required
                  minLength={8}
                  autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                  placeholder="••••••••"
                />
                {mode === 'signup' && (
                  <span className="text-[11px] text-slate-400">Mínimo 8 caracteres.</span>
                )}
              </label>
            )}

            {error && (
              <div className="px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-xs text-red-700">
                {error}
              </div>
            )}
            {info && (
              <div className="px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-200 text-xs text-emerald-700">
                {info}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="mt-2 px-4 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? 'Aguarde…' : submitLabel}
            </button>
          </form>

          <div className="mt-5 pt-4 border-t border-slate-100 text-center text-xs text-slate-500">
            {mode === 'login' && (
              <>Ainda não tem conta?{' '}
                <button
                  className="text-indigo-600 hover:underline font-medium"
                  onClick={() => { setMode('signup'); setError(null); setInfo(null); }}
                >Criar conta</button>
              </>
            )}
            {mode === 'signup' && (
              <>Já tem conta?{' '}
                <button
                  className="text-indigo-600 hover:underline font-medium"
                  onClick={() => { setMode('login'); setError(null); setInfo(null); }}
                >Entrar</button>
              </>
            )}
            {mode === 'forgot' && (
              <button
                className="text-indigo-600 hover:underline font-medium"
                onClick={() => { setMode('login'); setError(null); setInfo(null); }}
              >Voltar para login</button>
            )}
          </div>
        </div>

        <p className="mt-4 text-center text-[11px] text-slate-400">
          Ao continuar, você concorda com os termos de uso da NanoApp.
        </p>
      </div>
    </div>
  );
}
